/**
 * To Do This Week Card
 *
 * Lists tasks due this week that are not yet completed or cancelled.
 */

import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ListTodo } from 'lucide-react';
import { useTasks } from '@/features/operations-tasks/hooks/useTasks';

const PRIORITY_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  urgent: 'destructive',
  high: 'destructive',
  medium: 'default',
  low: 'secondary',
};

function formatDueDate(dateString?: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);

  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Overdue';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function TodoThisWeekCard() {
  const { data, isLoading } = useTasks({
    dueDatePreset: 'this_week',
    statuses: ['todo', 'in_progress', 'blocked'],
    pageLength: 8,
    sortKey: 'due_date',
    sortDirection: 1,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const tasks = data?.tasks ?? [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ListTodo className="h-4 w-4" />
          To Do This Week
          {tasks.length > 0 && (
            <Badge variant="secondary" className="ml-auto text-xs">
              {tasks.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {tasks.length > 0 ? (
          <ScrollArea className="h-[220px]">
            <div className="space-y-2 pr-4">
              {tasks.map((task) => (
                <Link
                  key={task.id}
                  to={`/operations/tasks?taskId=${task.id}`}
                  className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{task.title}</span>
                      <Badge
                        variant={PRIORITY_VARIANT[task.priority] ?? 'outline'}
                        className="text-xs shrink-0"
                      >
                        {task.priority}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">
                        {formatDueDate(task.due_date)}
                      </span>
                      {task.assigned_to_name && (
                        <>
                          <span className="text-xs text-muted-foreground">·</span>
                          <span className="text-xs text-muted-foreground truncate">
                            {task.assigned_to_name}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">
            No tasks due this week
          </div>
        )}
      </CardContent>
    </Card>
  );
}
